// Scored-calls heatmap: day-of-week (rows) x hour-of-day (cols).
import React, { useMemo } from 'react'

import { EChart } from '@/components/charts/EChart'
import { Card } from '@/components/cards/Card'
import { apiService } from '@/api'
import { browserTimezone } from '@/reducers/qa-copilot/qa-copilot.helpers'

import { CHART } from '@/components/charts/chartPalette'
import { useWidgetData } from './chartWidget'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 24 }, (_, hour) => hour.toString())

export const CallVolumeHeatmapWidget = ({ scope }) => {
  const timezone = useMemo(browserTimezone, [])
  const {
    data,
    loading,
    error: err,
  } = useWidgetData(scope, (params) => {
    params.set('timezone', timezone)
    return `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_hour_of_day?${params.toString()}`
  })
  const cells = data?.cells

  const option = useMemo(() => {
    // BE returns 1..7 (Mon..Sun) and omits empty cells; zero-fill and
    // 0-index for ECharts.
    const matrix = []
    for (let day = 0; day < 7; day += 1) {
      for (let hour = 0; hour < 24; hour += 1) {
        matrix.push([hour, day, 0])
      }
    }
    let maxCount = 0
    let totalCount = 0
    const safeCells = cells || []
    safeCells.forEach((cell) => {
      const dow = cell.day_of_week // 1..7 (Mon..Sun)
      if (dow < 1 || dow > 7) {
        return
      }
      const rowIdx = dow - 1
      const idx = rowIdx * 24 + cell.hour
      matrix[idx][2] = cell.count
      totalCount += cell.count
      if (cell.count > maxCount) {
        maxCount = cell.count
      }
    })
    if (totalCount === 0) {
      return { __empty: true }
    }
    return {
      tooltip: {
        formatter: (params) =>
          `${DAYS[params.data[1]]} ${params.data[0].toString().padStart(2, '0')}:00 — ${params.data[2].toLocaleString()} scored calls`,
      },
      // bottom 64 leaves room for BOTH the x-axis hour labels and the
      // horizontal visualMap slider pinned at bottom: 0.
      grid: { left: 50, right: 16, top: 32, bottom: 64 },
      xAxis: {
        type: 'category',
        data: HOURS,
        splitArea: { show: true },
        axisLabel: { interval: 1, fontSize: 10 },
      },
      yAxis: { type: 'category', data: DAYS, splitArea: { show: true } },
      visualMap: {
        // Endpoint text spells out what the color gradient means --
        // bare min/max numbers read as "0 to 1500 what?".
        min: 0,
        max: Math.max(1, maxCount),
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        itemWidth: 14,
        itemHeight: 140,
        textStyle: { fontSize: 10 },
        text: ['More scored calls', 'Fewer scored calls'],
        inRange: { color: [CHART.BRAND_BG, CHART.BRAND, CHART.BRAND_TEXT] },
      },
      series: [{ type: 'heatmap', data: matrix, label: { show: false } }],
    }
  }, [cells])

  // "Scored" in the title so it doesn't read as raw call volume; the
  // timezone renders as an inline subtitle.
  const cardTitle = (
    <>
      Scored Calls by Hour of Week <span className="card-subtitle">· {timezone}</span>
    </>
  )
  return (
    <Card title={cardTitle} info="When calls happen — call volume by day of week and hour of day.">
      {err && <div className="entity-drawer-error">{err}</div>}
      {!err && option.__empty && (
        <div className="entity-drawer-empty">No scored calls in this window.</div>
      )}
      {!err && !option.__empty && (
        <EChart option={option} loading={loading} style={{ height: '300px' }} />
      )}
    </Card>
  )
}
